import { Box, Flex, Text } from "@radix-ui/themes";
import { Subscription } from "@/types/breeze/AnythinkPay";
import { formatDate, formatRelativeDate } from "@/types/breeze/AnythinkPay";
import { Calendar, CreditCard, XCircle, Clock } from "lucide-react";

interface BillingTimelineProps {
  subscription: Subscription;
}

export function BillingTimeline({ subscription }: BillingTimelineProps) {
  const events: Array<{
    date: Date | null;
    label: string;
    icon: React.ReactNode;
    color: string;
    isPast: boolean;
  }> = [];

  // Current period start
  if (subscription.current_period_starts_at) {
    const date = new Date(subscription.current_period_starts_at);
    events.push({
      date,
      label: "Current Period Started",
      icon: <Calendar size={16} />,
      color: "var(--blue-9)",
      isPast: date < new Date(),
    });
  }

  // Trial end
  if (subscription.trial_ends_at) {
    const date = new Date(subscription.trial_ends_at);
    events.push({
      date,
      label: "Trial Ends",
      icon: <Clock size={16} />,
      color: "var(--orange-9)",
      isPast: date < new Date(),
    });
  }

  // Next billing / Current period end
  if (subscription.current_period_ends_at) {
    const date = new Date(subscription.current_period_ends_at);
    events.push({
      date,
      label: "Next Billing Date",
      icon: <CreditCard size={16} />,
      color: "var(--green-9)",
      isPast: date < new Date(),
    });
  }

  // Scheduled cancellation
  if (subscription.auto_cancel_at) {
    const date = new Date(subscription.auto_cancel_at);
    events.push({
      date,
      label: "Scheduled Cancellation",
      icon: <XCircle size={16} />,
      color: "var(--red-9)",
      isPast: date < new Date(),
    });
  }

  // Sort events by date
  events.sort((a, b) => {
    if (!a.date) return 1;
    if (!b.date) return -1;
    return a.date.getTime() - b.date.getTime();
  });

  if (events.length === 0) {
    return (
      <Box>
        <Text size="2" color="gray">
          No billing timeline information available
        </Text>
      </Box>
    );
  }

  return (
    <Box>
      <Flex direction="column" gap="3">
        {events.map((event, index) => (
          <Flex key={index} align="start" gap="3">
            <Box
              style={{
                padding: "8px",
                borderRadius: "4px",
                background: event.isPast ? "var(--gray-3)" : `${event.color}20`,
                color: event.isPast ? "var(--gray-9)" : event.color,
              }}
            >
              {event.icon}
            </Box>
            <Flex direction="column" gap="2" style={{ flex: 1 }}>
              <Text
                size="2"
                weight={event.isPast ? "regular" : "bold"}
                color={event.isPast ? "gray" : undefined}
              >
                {event.label}
              </Text>
              {event.date && (
                <Flex align="center" gap="2">
                  <Text size="2" color="gray">
                    {formatRelativeDate(event.date)}
                  </Text>
                  <Text size="1" color="gray">
                    ({formatDate(event.date)})
                  </Text>
                </Flex>
              )}
            </Flex>
          </Flex>
        ))}
      </Flex>
    </Box>
  );
}
